import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveBaseProductBarcode } from '../../AIGeneration.helper';
import ProductPomMeasurementQuery from '@/services/query/productPomMeasurement/ProductPomMeasurementQuery';
import {
    formatBedenBilgileriText,
    ProductAttributeItem,
    ProductPomMeasurementSizeGroup,
} from '@/services/query/productPomMeasurement/productPomMeasurement.types';

interface UseHangerCardSizeInfoParams {
    barcode: string;
    selectedSizeCode?: string;
    sizes: ProductPomMeasurementSizeGroup[];
    productAttributes: ProductAttributeItem[];
    sizeInfoLoaded?: boolean;
    onSizeSelectionChange: (sizeCode: string, measurementText: string) => void;
    onSizeInfoDataChange: (
        sizes: ProductPomMeasurementSizeGroup[],
        productAttributes: ProductAttributeItem[],
        loaded: boolean
    ) => void;
}

export const useHangerCardSizeInfo = ({
    barcode,
    selectedSizeCode = '',
    sizes,
    productAttributes,
    sizeInfoLoaded = false,
    onSizeSelectionChange,
    onSizeInfoDataChange,
}: UseHangerCardSizeInfoParams) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const trimmedBarcode = barcode?.trim() ?? '';
    const apiBarcode = resolveBaseProductBarcode(trimmedBarcode);
    const prevBarcodeRef = useRef<string | null>(null);

    const applySizeSelection = useCallback(
        (sizeGroup: ProductPomMeasurementSizeGroup | undefined, attributes: ProductAttributeItem[]) => {
            if (!sizeGroup) {
                onSizeSelectionChange('', formatBedenBilgileriText([], attributes));
                return;
            }

            onSizeSelectionChange(
                sizeGroup.sizeCode,
                formatBedenBilgileriText(sizeGroup.measurements, attributes)
            );
        },
        [onSizeSelectionChange]
    );

    const resolveFetchError = useCallback(
        (err: unknown): string => {
            if (!/^\d+$/.test(apiBarcode)) {
                return t('mannequin.aiGeneration.configuration.sizeInfo.barcodeNumericOnly');
            }

            const status = (err as { status?: number })?.status;
            const data = (err as { data?: any })?.data;
            const apiMessage =
                data?.message ||
                data?.Message ||
                data?.title ||
                data?.Title ||
                data?.errors?.Barcode?.[0] ||
                data?.errors?.barcode?.[0];

            if (typeof apiMessage === 'string' && apiMessage.trim()) {
                return apiMessage;
            }

            if (status === 400) {
                return t('mannequin.aiGeneration.configuration.sizeInfo.barcodeNumericOnly');
            }

            if (status === 404) {
                return t('mannequin.aiGeneration.configuration.sizeInfo.empty');
            }

            return t('mannequin.aiGeneration.configuration.sizeInfo.error');
        },
        [apiBarcode, t]
    );

    const fetchSizeInfo = useCallback(async () => {
        if (!trimmedBarcode) {
            onSizeInfoDataChange([], [], false);
            setError(null);
            applySizeSelection(undefined, []);
            return;
        }

        if (!/^\d+$/.test(apiBarcode)) {
            onSizeInfoDataChange([], [], true);
            applySizeSelection(undefined, []);
            setError(t('mannequin.aiGeneration.configuration.sizeInfo.barcodeNumericOnly'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await ProductPomMeasurementQuery.GetByBarcode(apiBarcode);
            const nextSizes = data?.sizes ?? [];
            const nextAttributes = data?.productAttributes ?? [];
            onSizeInfoDataChange(nextSizes, nextAttributes, true);

            if (nextSizes.length === 0) {
                applySizeSelection(undefined, nextAttributes);
                return;
            }

            const matched =
                nextSizes.find((size) => size.sizeCode === selectedSizeCode) ?? nextSizes[0];
            applySizeSelection(matched, nextAttributes);
        } catch (err) {
            onSizeInfoDataChange([], [], true);
            applySizeSelection(undefined, []);
            setError(resolveFetchError(err));
        } finally {
            setLoading(false);
        }
    }, [
        trimmedBarcode,
        apiBarcode,
        selectedSizeCode,
        applySizeSelection,
        onSizeInfoDataChange,
        resolveFetchError,
        t,
    ]);

    useEffect(() => {
        if (prevBarcodeRef.current === null) {
            prevBarcodeRef.current = trimmedBarcode;
            return;
        }

        if (prevBarcodeRef.current === trimmedBarcode) {
            return;
        }

        prevBarcodeRef.current = trimmedBarcode;
        onSizeSelectionChange('', '');
        onSizeInfoDataChange([], [], false);
        setError(null);
    }, [trimmedBarcode, onSizeSelectionChange, onSizeInfoDataChange]);

    const handleSizeCodeChange = useCallback(
        (sizeCode: string) => {
            const sizeGroup = sizes.find((size) => size.sizeCode === sizeCode);
            applySizeSelection(sizeGroup, productAttributes);
        },
        [sizes, productAttributes, applySizeSelection]
    );

    return {
        trimmedBarcode,
        loading,
        error,
        fetched: sizeInfoLoaded,
        sizes,
        productAttributes,
        fetchSizeInfo,
        handleSizeCodeChange,
    };
};
